import { useAppContext } from 'Context/ReactAppContext';
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { t } from 'Src/i18n';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import URI from 'urijs';
import type { PrebookingFormBaseProps } from './PrebookingForm';
import { PrebookingForm } from './PrebookingForm';
import type { FormValues, InitialFormData } from './PrebookingForm.utils';
import {
  getFormOptins,
  getFormSchema,
  getFormUser,
  getInitialFormValues,
  handleSubmit,
} from './PrebookingForm.utils';
type State = {
  userInfo: UserInfo;
  tripInformation: {
    adults: Array<Adult>;
    kids: Array<Kid>;
  };
};
type PrebookingFormState = {
  initialFormData: InitialFormData;
};
type PrebookingFormContainerProps = PrebookingFormBaseProps & {
  redirectUris: {
    error: null | string;
    success: null | string;
  };
};
export function PrebookingFormContainer({
  adultsOptions,
  agencies,
  civilities,
  dateFormat,
  departureDateEnd,
  departureDateStart,
  hasUnicodeFields,
  hasUser,
  kidsOptions,
  consentOptins,
  productOptions,
  products,
  redirectUris,
}: PrebookingFormContainerProps) {
  const appContext = useAppContext();
  const router = useRouter();
  const { productId } = router.query;
  const productIdRef = useRef(null);
  const [proposal, setProposal] = React.useState(null);
  const [formStep, setFormStep] = React.useState(0);
  const [hasError, setHasError] = React.useState(false);

  const scrollToFormStep = (step) => {
    let attemptCount = 0;
    const maxAttempts = 5;

    const checkStepAndScroll = () => {
      attemptCount += 1;
      const formStepElement = document.getElementById(`pbf-step-${step}-title`);

      if (formStepElement) {
        scrollToElement(`#pbf-step-${step}-title`, 500, '.pbf-loader', false, step === 1 ? 50 : 0);
      } else if (attemptCount < maxAttempts) {
        setTimeout(checkStepAndScroll, 100);
      }
    };

    setFormStep(step);
    checkStepAndScroll();
  };

  const redirects = {
    errorUrl: redirectUris.error || '/',
    successUrl: redirectUris.success || '/',
  };
  const productIds = productOptions.map((productOption) => productOption.value).filter(Boolean);
  const { trackClickEvent } = useTrackEvent();
  useEffect(() => {
    if (productId) {
      productIdRef.current = productId;
      const newUri = new URI(router.asPath).removeQuery(['productId']);
      router.replace(newUri.toString(), undefined, {
        shallow: true,
      });
    }
  }, []);
  const { initialFormData } = useSelector((state: State): PrebookingFormState => {
    const { userInfo } = state;
    const intialProductId =
      productOptions.find((product) => product.value === productIdRef.current)?.value ||
      productOptions[0]?.value ||
      '';
    return {
      initialFormData: {
        productId: intialProductId,
        traveler: getFormUser(userInfo),
        ...getFormOptins(userInfo, consentOptins),
      },
    };
  });
  const schema = getFormSchema(
    t,
    {
      agencies: agencies.map((agency) => agency.value),
      civilities,
      locale: appContext.locale,
      productIds,
    },
    formStep,
  );
  return (
    <Formik
      initialValues={getInitialFormValues(initialFormData)}
      validationSchema={schema}
      onSubmit={async (values: FormValues) => {
        return handleSubmit(values, {
          appContext,
          dateFormat,
          redirects,
          router,
          trackClickEvent,
          setHasError,
          proposalId: proposal?.id,
          proposalPrice: proposal?.price,
        });
      }}
    >
      <PrebookingForm
        adultsOptions={adultsOptions}
        agencies={agencies}
        civilities={civilities}
        dateFormat={dateFormat}
        departureDateEnd={departureDateEnd}
        departureDateStart={departureDateStart}
        hasError={hasError}
        hasUnicodeFields={hasUnicodeFields}
        hasUser={hasUser}
        kidsOptions={kidsOptions}
        consentOptins={consentOptins}
        productOptions={productOptions}
        products={products}
        proposal={proposal}
        setProposal={setProposal}
        formStep={formStep}
        setFormStep={scrollToFormStep}
        setHasError={setHasError}
      />
    </Formik>
  );
}
